import { DraggableRowsTable } from '@components/draggable-rows-table';
import { EntitySearchMultiParty, PersonSearch } from '@components/entity-search';
import {
  QueryParamsAdapter,
  useFormSnackbarError,
  usePintMaster,
  useRecentCallback,
  useRequest,
} from '@lib/common';
import { DealEntity, EntityType } from '@lib/deals';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Fab,
  FormControl,
  Grid,
  InputLabel,
  Select,
  TextField,
  Tooltip,
  Typography,
} from '@material-ui/core';
import styled from 'styled-components';
import { AddIcon } from '@material-ui/data-grid';
import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { Controller, FormProvider, useController, useForm, useFormContext } from 'react-hook-form';
import { Column } from 'react-table';
import { ContentEntity } from './types';
import { startCase } from 'lodash';
import { ContentType as ContentTypeEnum } from '@lib/content';
import { GenresTypeField } from '@pages/content/form/fields/content-genres-type';
import { ControlledAutoCompleteFieldForContentKeywords } from '@pages/content/form/fields/content-keywords.field';
import { LanguageField } from '@pages/content/form/fields/content-language.field';
import { ContentStatusField } from '@pages/content/form/fields/content-status.field';
import { FilmingLocationField } from '@pages/deals/deal-form/filming-location-field';
import { GenresField } from '@pages/media/form/fields/content-genres.field';
import { OriginCountryField } from 'pages/content/form/fields/content-country.field';
import { PageLoading } from '@components/page-loading';
import { vestResolver } from '@hookform/resolvers/vest';
import { dialogContentValidationSuite } from '@pages/deals/deal-form/validators';
import { useSnackbar } from 'notistack';
import { finalize } from 'rxjs/operators';
import * as ContentGenres from '@api/content-genres';
import * as contentService from '@api/content';
import { RefreshCounterProvider } from '@pages/content/search/content-search';

export const CONTENT_TO_PERSON = 'content2person';

export function ContentPersonRelationForm({ relationData, createRelation = false }) {
  const form = useFormContext();
  const [refreshCounter, setRefreshCounter] = useState(0);
  const params = QueryParamsAdapter.fromParams(location.search);
  const originalRelationId = params?.originalRelationId;
  const [contents, setContents] = useState(() => relationData?.contents || []);
  const [activeContentIndex, setActiveContentIndex] = useState<number | null>(null);

  // code to add multiple content
  const [indexesDealBuyers, setIndexesDealBuyers] = useState(
    Array.from(Array(relationData.contents.length), (_, i) => i)
  );
  const [counterDealBuyers, setCounterDealBuyers] = useState(1);
  const addDealBuyers = useCallback(() => {
    const watchOnBuyers = form.watch('associate_person');
    setIndexesDealBuyers(prevIndexes => {
      return [...prevIndexes, counterDealBuyers];
    });
    setCounterDealBuyers(prevCounter => prevCounter + 1);
  }, [indexesDealBuyers, counterDealBuyers]);

  const removeDealBuyers = useCallback(
    index => {
      setIndexesDealBuyers(prevIndexes => {
        return [...prevIndexes.filter(item => item !== index)];
      });
      const watchOnBuyers = form.watch('contents');
      watchOnBuyers
        .filter((x, idx) => idx !== index)
        .forEach((eachRow, idx) => {
          if (eachRow) eachRow['order_id'] = idx + 1;
        });

      setCounterDealBuyers(prevCounter => prevCounter - 1);
    },
    [relationData]
  );

  //Create New nonIdContentForm
  const [isNonIdContent, setIsNonIdContent] = useState(false);
  const [searchTitle, setSearchText] = useState('');
  const [openNonIdModal, setOpenNonIdModal] = useState(false);

  const addNonIdContentForm = (contentTitle, index) => {
    setSearchText(contentTitle);
    setActiveContentIndex(index);
    setIsNonIdContent(true);
    setOpenNonIdModal(true); // <-- open modal
  };

  const columns: Column<any>[] = useMemo(
    () => [
      // {
      //   Header: '#',
      //   id: 'serial',
      //   accessor: (_row, idx) => idx + 1,
      //   width: 35,
      // },
      {
        Header: 'Cell',
        id: 'cell',
        // width: 200,
        Cell: ({ row, idx }) => {
          const isRowDeletetable = row.original.deleteable;
          const index = row.original.id;
          const fieldName = `contents[${index}]`;
          return (
            <Controller
              name={fieldName}
              key={index}
              control={form.control}
              defaultValue={
                contents[index]
                  ? contents[index]
                  : {
                      name: '',
                      type: EntityType.content,
                      vtr_id: '',
                      meta: null,
                      relation_type: 'content',
                      order_id: index + 1,
                    }
              }
              render={({ onChange }) => (
                <PaddingBottomEntitySearch key={index}>
                  <EntitySearchMultiParty
                    label="Content"
                    fixedType={EntityType.content}
                    defaultValue={
                      contents[index]
                        ? contents[index]
                        : {
                            name: '',
                            type: EntityType.content,
                            vtr_id: '',
                            meta: null,
                            relation_type: 'content',
                            order_id: index + 1,
                          }
                    }
                    hideTypeSelect
                    clearable
                    onChange={onChange}
                    // changeBuyer={changeBuyer}
                    titleEditable
                    indexOfEntities={index}
                    removeDealEntities={removeDealBuyers}
                    isDealEntityRemovable={isRowDeletetable}
                    dealData={form.getValues()}
                    // isDealEntityRequestCreatable={!createDeal}
                    txtMessage="CONTENT"
                    page={CONTENT_TO_PERSON}
                    addNonIdContentForm={(title) => addNonIdContentForm(title, index)}
                  />
                </PaddingBottomEntitySearch>
              )}
            />
          );
        },
      },
    ],
    [relationData.contents, contents]
  );

  const getRowId = React.useCallback((row: any, idx) => {
    return '' + row.id;
  }, []);

  const onChangeDraggedRows = allRowsModified => {
    const watchOnBuyers = form.watch('contents');
    const modifiedRowsWithOrderId = allRowsModified.map((eachRow, idx) => {
      if (watchOnBuyers[eachRow.id]) {
        watchOnBuyers[eachRow.id]['order_id'] = idx + 1;
        return { ...watchOnBuyers[eachRow.id], order_id: idx + 1 };
      }
      return watchOnBuyers[eachRow.id];
    });
    setIndexesDealBuyers(allRowsModified.map(eachRow => eachRow.id));
    // form.setValue('ordered_buyers', modifiedRowsWithOrderId);
  };
  return (
    <div>
      {!createRelation || originalRelationId ? (
        <div>
          <Typography variant="h6" gutterBottom>
            Content
          </Typography>
          <Controller
            name="contents"
            control={form.control}
            defaultValue={
              relationData.contents
                ? relationData.contents
                : {
                    name: '',
                    type: EntityType.content,
                    vtr_id: '',
                    meta: null,
                    relation_type: 'content',
                    order_id: 1,
                  }
            }
            render={({ onChange }) => (
              <>
                {/*  <RefreshCounterProvider
                  refreshCounter={refreshCounter}
                  forceRefresh={() => setRefreshCounter(counter => counter + 1)}>
                  */}{' '}
                <PaddingBottomEntitySearch>
                  <EntitySearchMultiParty
                    label="Content"
                    fixedType={EntityType.content}
                    defaultValue={
                      contents
                        ? contents
                        : {
                            name: '',
                            type: EntityType.content,
                            vtr_id: '',
                            meta: null,
                            relation_type: 'content',
                            order_id: 1,
                          }
                    }
                    hideTypeSelect
                    clearable
                    onChange={onChange}
                    // titleEditable
                    indexOfEntities={1}
                    page={CONTENT_TO_PERSON}
                  />
                </PaddingBottomEntitySearch>
                {/* </RefreshCounterProvider> */}
              </>
            )}
          />{' '}
        </div>
      ) : (
        <div>
          <div style={{ display: 'flex' }}>
            <Typography variant="h6" gutterBottom>
              Content
            </Typography>
            <Grid item style={{ paddingRight: '20px', paddingBottom: '10px', marginLeft: 'auto' }}>
              {/* <div> */}
              <Tooltip title="Add Content">
                <StyledFab
                  onClick={addDealBuyers}
                  disabled={false}
                  color="primary"
                  aria-label="add"
                  size="small">
                  <AddIcon />
                </StyledFab>
              </Tooltip>
              {/* </div> */}
            </Grid>
          </div>
          <fieldset style={{ padding: '0.75em', backgroundColor: '#f5f5f5', border: 'none' }}>
            <DraggableRowsTableStyled
              getRowId={getRowId}
              onChange={onChangeDraggedRows}
              columns={columns}
              data={indexesDealBuyers.map(idx => ({
                id: idx,
                deleteable: indexesDealBuyers.length > 1,
              }))}
              stickyHeader
              size="small"
              hideHeaders
              showDragIcon={false}
            />

            <CreateContent
              contents={form.watch('contents')}
              open={openNonIdModal}
              onClose={() => setOpenNonIdModal(false)}
              onCancel={() => setOpenNonIdModal(false)}
              searchContentTitle={searchTitle}
              onSuccess={newContent => {
                setOpenNonIdModal(false);
                if (activeContentIndex !== null) {
                  const serialized = {
                    name:
                      newContent?.title ||
                      newContent?.meta?.title ||
                      newContent?.meta?.original_title,
                    vtr_id: newContent?.vtr_id || newContent?.meta?.vtr_id,
                    meta: newContent,
                    type: EntityType.content,
                    relation_type: 'content',
                    order_id: activeContentIndex + 1,
                  };

                  // 1️⃣ Update contents (drives EntitySearchMultiParty)
                  setContents(prev => {
                    const updated = [...prev];
                    updated[activeContentIndex] = serialized;
                    return updated;
                  });

                  // 2️⃣ Sync RHF
                  form.setValue(`contents.${activeContentIndex}`, serialized, {
                    shouldDirty: true,
                  });
                }
              }}
            />
          </fieldset>
        </div>
      )}
    </div>
  );
}

const PaddingBottomEntitySearch = styled.div`
  margin-bottom: 20px;
  :last-child {
    margin-bottom: 0px;
  }
`;

const StyledFab = styled(Fab)`
  &.MuiFab-sizeSmall {
    width: 40px;
    // height: 30px;
  }
  & .MuiSvgIcon-root {
    font-size: 1.4rem;
  }
`;

const DraggableRowsTableStyled = styled(DraggableRowsTable)`
  & .MuiTableCell-root.MuiTableCell-body.MuiTableCell-sizeSmall {
    padding-bottom: 10px;
    padding-top: 10px;
  }
`;

/* const ControlledAutoCompleteFieldPersonSearch = ({ name, label, control, ...props }) => {
  const {
    field: { value, onChange },
  } = useController({ name, control });
  return (
    <PersonSearch
      associatedPerson={value}
      setAssociatedPerson={onChange}
      multiple={true}
      label={label}
      //disabled={true}
    />
  );
}; */

export function CreateContent({
  contents,
  onOk = null,
  onCancel = null,
  open,
  onClose,
  loading = false,
  onSuccess,
  searchContentTitle,
}) {
  const defaultValuesForDialogContent = {
    name: '',
    vtr_id: '',
    meta: null,
    relation_type: 'content',
    order_id: 1,
    ...contents[1],
    type: EntityType.content,
  };
  const { loading: genreLoading, result: genresMasterOptions } = useRequest(
    () => ContentGenres.get(),
    []
  );
  const { loading: languageLoading, master: languages } = usePintMaster('languages');

  const { loading: loadingCountries, master: countries } = usePintMaster('countries');

  const Not_Selected = 'Not Selected';
  const Not_Selected_Val = -1;

  const [isNonIdContent, setIsNonIdContent] = useState(true);
  const [searchText, setSearchText] = useState("");

useEffect(() => {
  if (open) {
    form.reset({
      ...form.getValues(),
      title: searchContentTitle || '',
    });
    setSearchText(searchContentTitle || '');
  }
}, [open, searchContentTitle]);

  const contentTypeObj = {
    [Not_Selected]: -1,
    Movie: 0,
    'TV Series': 1,
    Book: 2,
    Podcast: 3,
    Game: 4,
    Format: 5,
    Story: 6,
    IP: 7,
    'Micro-Series': 8,
  };

  const contentTypeCodeToName = {
    [Not_Selected_Val]: 'Not Selected',
    0: 'Movie',
    1: 'TV Series',
    2: 'Book',
    3: 'Podcast',
    4: 'Game',
    5: 'Format',
    6: 'Story',
    7: 'IP',
    8: 'Micro-Series'
  };

  const form = useForm({
    defaultValues: {
      dialogContent: defaultValuesForDialogContent,
      book_name: defaultValuesForDialogContent.book_name
        ? defaultValuesForDialogContent.book_name
        : null,
      country_of_origin: [],
      deal_content_genre: [],
      deal_season: defaultValuesForDialogContent.deal_season
        ? defaultValuesForDialogContent.deal_season
        : null,
      working_title: defaultValuesForDialogContent.working_title
        ? defaultValuesForDialogContent.working_title
        : null,
      deal_content_type: defaultValuesForDialogContent.deal_content_type
        ? defaultValuesForDialogContent.deal_content_type
        : Not_Selected,
      based_on: defaultValuesForDialogContent.based_on
        ? defaultValuesForDialogContent.based_on
        : null,
      local_content_name: defaultValuesForDialogContent.local_content_name
        ? defaultValuesForDialogContent.local_content_name
        : null,
      local_content_vtr_id: defaultValuesForDialogContent.local_content_vtr_id
        ? defaultValuesForDialogContent.local_content_vtr_id
        : null,
    /*   cast_and_crew: defaultValuesForDialogContent.cast_and_crew
        ? defaultValuesForDialogContent.cast_and_crew
        : [], */
      content_id: '',
      genre_category: '',
      status: [] as unknown as { name: string },
      origin_country: [],
      genres_id: [],
      language_id: [] as unknown as { id: string },
      filming_location: [],
      content_keywords: defaultValuesForDialogContent?.content_keywords
        ? typeof defaultValuesForDialogContent?.content_keywords === 'string'
          ? defaultValuesForDialogContent?.content_keywords
              ?.split(',')
              ?.map(key => key?.trim())
              ?.filter(key => key)
          : defaultValuesForDialogContent?.content_keywords
        : [],
      title: searchText || searchContentTitle||'',
      content_genre: '',
      content_type: defaultValuesForDialogContent.deal_content_type
        ? contentTypeObj[defaultValuesForDialogContent.deal_content_type]
        : Not_Selected_Val,
    },
    resolver: vestResolver(dialogContentValidationSuite),
    reValidateMode: 'onSubmit',
  });

  useEffect(() => {
    if (
      genresMasterOptions &&
      genresMasterOptions?.data &&
      genresMasterOptions?.data?.length > 0 &&
      defaultValuesForDialogContent?.deal_content_genre
    ) {
      const getGenreObjByNames = (genreNames: string) => {
        const genreValue = Array.isArray(genreNames) ? genreNames : genreNames?.split(',');
        const mappedGenreNamesArray =
          genreValue?.filter(name => name)?.map(name => name?.trim()) ?? [];
        const genreObjArray =
          mappedGenreNamesArray
            ?.map(genre_name => genresMasterOptions?.data?.find(({ name }) => name == genre_name))
            .filter(obj => obj) ?? [];
        form.setValue('deal_content_genre', genreObjArray);
      };
      getGenreObjByNames(defaultValuesForDialogContent?.deal_content_genre);
    }
  }, [genresMasterOptions]);

  useEffect(() => {
    if (countries && countries?.length > 0 && defaultValuesForDialogContent?.country_of_origin) {
      const getCountryObjByNames = (countryNames: string) => {
        const countryValue = Array.isArray(countryNames) ? countryNames : countryNames?.split(',');
        const mappedCountryNamesArray =
          countryValue?.filter(name => name)?.map(name => name?.trim()) ?? [];
        const countryObjArray =
          mappedCountryNamesArray
            ?.map(genre_name => countries?.find(({ name }) => name == genre_name))
            .filter(obj => obj) ?? [];
        form.setValue('country_of_origin', countryObjArray);
      };
      getCountryObjByNames(defaultValuesForDialogContent?.country_of_origin);
    }
  }, [countries]);

  const [onFinish] = useRecentCallback(dialogData => {
    if (dialogData?.content_type === Not_Selected_Val) {
      enqueueSnackbar(`Please select a content type.`, {
        variant: 'error',
      });
      return;
    }
    const requiredData = {
      ...dialogData.dialogContent,
      book_name: dialogData.book_name,
      country_of_origin:
        dialogData?.country_of_origin?.map(({ name = '' }) => name)?.join(', ') ?? '',
      origin_country: dialogData?.country_of_origin?.map(({ id = '' }) => id)?.join(',') ?? '',
      deal_content_genre:
        dialogData?.deal_content_genre?.map(({ name = '' }) => name)?.join(', ') ?? '',
      deal_content_genre_ids:
        dialogData?.deal_content_genre?.map(({ id = '' }) => id)?.join(',') ?? '',
      content_keywords: dialogData?.content_keywords?.join(', '),
      deal_season: dialogData.deal_season,
      working_title: dialogData.working_title,
      deal_content_type:
        dialogData?.content_type || dialogData?.content_type === ContentTypeEnum?.MOVIE
          ? contentTypeCodeToName[dialogData?.content_type]
          : Not_Selected,
      based_on: dialogData.based_on,
      local_content_name: dialogData.local_content_name,
      local_content_vtr_id: dialogData.local_content_vtr_id,
    //  cast_and_crew: dialogData.cast_and_crew,
    };
    onOk(requiredData);
    onSuccess?.(dialogData);
  });

  const getGenreNameFromId = genre_ids => {
    if (genre_ids) {
      const genreIdArr = genre_ids?.split(',') ?? [];
      const genreObjArr = genreIdArr
        ?.map(genre_id => genresMasterOptions?.data?.find(({ id = '' }) => id == genre_id))
        .filter(obj => obj);
      const genreName = genreObjArr?.map(({ name = '' }) => name)?.join(', ');
      return genreName;
    }
    return [];
  };

  const getGenreObjFromId = genre_ids => {
    if (genre_ids) {
      const genreIdArr = genre_ids?.split(',') ?? [];
      const genreObjArr = genreIdArr
        ?.map(genre_id => genresMasterOptions?.data?.find(({ id = '' }) => id == genre_id))
        .filter(obj => obj);
      return genreObjArr;
    }
    return [];
  };

  const getLanguageNameFromId = language_ids => {
    if (language_ids) {
      const languageIdArr = language_ids?.split(',') ?? [];
      const languageObjArr = languageIdArr?.map(language_id =>
        languages?.find(({ id = '' }) => id == language_id.toLowerCase())
      );
      const languageObjName = languageObjArr?.map(({ name = '' }) => name)?.join(', ');
      return languageObjName;
    }
  };

  const getCountryNameFromId = country_ids => {
    if (country_ids) {
      const countryIdArr = country_ids?.split(',') ?? [];
      const countryObjArr = countryIdArr?.map(country_id =>
        countries?.find(({ id = '' }) => id == country_id)
      );
      const countryObjName = countryObjArr?.map(({ name = '' }) => name)?.join(', ');
      return countryObjName;
    }
    return '';
  };

  const getCountryObjFromId = country_ids => {
    if (country_ids) {
      const countryIdArr = country_ids?.split(',') ?? [];
      const countryObjArr = countryIdArr
        ?.map(country_id => countries?.find(({ id = '' }) => id == country_id))
        .filter(obj => obj);
      return countryObjArr;
    }
    return [];
  };

  const serializeContent = row => {
    const payload = form?.getValues();
    const obj = {
      name: row?.title || row?.original_title,
      vtr_id: row?.vtr_id ?? '',
      meta: row,
      type: EntityType.content,
      content_genre: getGenreNameFromId(row?.genres_id),
      content_genre_ids: row?.genres_id,
      language: row?.language_id,
      country_of_origin: getCountryNameFromId(row?.origin_country),
      origin_country: row?.origin_country,
      imdb_id: row?.imdb_id ? row?.imdb_id : null,
      deal_content_type:
        row?.content_type || row?.content_type === ContentTypeEnum?.MOVIE
          ? contentTypeCodeToName[row?.content_type]
          : '',
      working_title: row?.working_title ? row?.working_title : '',
      book_name: null,
      content_keywords: row?.content_keywords
        ? row?.content_keywords?.filter(key => key)?.join(',')
        : '',
      deal_season: payload?.deal_season ? payload?.deal_season : '',
     /*  cast_and_crew:
        row?.cast_and_crew && Array.isArray(row?.cast_and_crew) ? row?.cast_and_crew : [], */
      original_title: row?.original_title,
      source_name: 'Deli',
    };
    multiPartyRef.current?.setPartialEntity(obj);
    onOk?.(obj);
    onSuccess?.(obj);
  };

  const multiPartyRef = useRef(null);

  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  const createUnIdidContent = () => {
    const payload = form.getValues();
    const createContentPayload = {
      content_type: payload?.content_type ?? 1,
      working_title: payload?.working_title ?? '',
     cast_crew: [],
      genre_category: payload?.genre_category ?? '',
      status: payload?.status?.name ?? '',
      "origin_country": payload?.origin_country?.map((country) => country?.id)?.join(","),
      genres_id: payload?.genres_id?.map(genre => genre?.id)?.join(','),
      language_id: payload?.language_id?.id,
      filming_location: payload?.filming_location ?? [],
      content_keywords: payload?.content_keywords ?? [],
      content_id: null,
      vtr_content_status: '',
      title: payload?.title,
      adult: 0,
      dataowner_id: 4001,
      content_publish_ready: -2,
      source_name: 'Deli',
      //   "source_url": id
    };
    if (!createContentPayload?.title) {
      enqueueSnackbar(`Content Title cannot be empty.`, {
        variant: 'error',
      });
      return;
    }
    if (!createContentPayload?.content_type && createContentPayload?.content_type !== 0) {
      enqueueSnackbar(`Please select content type.`, {
        variant: 'error',
      });
      return;
    }
    if (createContentPayload?.content_type === Not_Selected_Val) {
      enqueueSnackbar(`Please select a content type.`, {
        variant: 'error',
      });
      return;
    }
    const infoNotification = enqueueSnackbar('Please wait creating new content', {
      variant: 'info',
    });
    contentService
      .create(createContentPayload)
      .pipe(finalize(() => closeSnackbar(infoNotification)))
      .subscribe({
        next: response => {
          const data = response?.data ? response?.data : {};
          serializeContent(data);
          if (response.success) {
            onSuccess?.(data); // <-- Close modal + refresh
          }
          enqueueSnackbar('New Content created Successfully!', { variant: 'success' });
        },
        error: err => {
          enqueueSnackbar(`Error creating new content (${err?.response?.data?.error})`, {
            variant: 'error',
          });
        },
        complete: () => closeSnackbar(infoNotification),
      });
  };

  const formError = useFormSnackbarError();

  const submitForm = form.handleSubmit(onFinish, formError);
  const fieldName = `dialogContent`;
  const content = form.watch('dialogContent');
  const content_type = form.watch('content_type');
  const disableSeasonInput = content?.meta?.content_type === 'Movie';
  const isMovie = content_type == ContentTypeEnum.MOVIE;
  (disableSeasonInput || isMovie) && form.setValue('deal_season', null);
  const vtrIdChosenForContent = content?.vtr_id;

  useEffect(() => {
    if (content && content?.meta && content?.meta?.content_type) {
      form.setValue('content_type', contentTypeObj[content?.meta?.content_type], {
        shouldDirty: true,
      });
    }
    if (
      content &&
      content?.meta &&
      (content?.meta?.genres_id ||
        content?.meta?.genres_id === null ||
        content?.meta?.genres_id === '')
    ) {
      const genreObj = getGenreObjFromId(content?.meta?.genres_id ?? '');
      form.setValue('deal_content_genre', genreObj);
    }
    if (
      content &&
      content?.meta &&
      (content?.meta?.origin_country ||
        content?.meta?.origin_country === '' ||
        content?.meta?.origin_country === null)
    ) {
      const countryObj = getCountryObjFromId(content?.meta?.origin_country ?? '');
      form.setValue('country_of_origin', countryObj);
    }
   /*  if (
      content &&
      content?.meta &&
      (content?.meta?.cast_and_crew ||
        content?.meta?.cast_and_crew === '' ||
        content?.meta?.cast_and_crew === null)
    ) {
      form.setValue('cast_and_crew', content?.meta?.cast_and_crew ?? []);
    } */
    if (
      content &&
      content?.meta &&
      (content?.meta?.content_keywords ||
        content?.meta?.content_keywords === '' ||
        content?.meta?.content_keywords === null)
    ) {
      const content_keywords = content?.meta?.content_keywords ?? '';
      if (typeof content_keywords === 'string') {
        const content_key_arr = content_keywords ? content_keywords?.split(',') : [];
        form.setValue('content_keywords', content_key_arr);
      }
    }
    if (
      content &&
      content?.meta &&
      (content?.meta?.working_title ||
        content?.meta?.working_title === '' ||
        content?.meta?.working_title === null)
    ) {
      const working_title = content?.meta?.working_title ?? '';
      form.setValue('working_title', working_title);
    }
    if (content && content?.meta === null) {
      form.setValue('working_title', '');
      form.setValue('content_keywords', []);
     // form.setValue('cast_and_crew', []);
      form.setValue('country_of_origin', []);
      form.setValue('deal_content_genre', []);
    }
  }, [content]);

  const ContentType = useCallback(() => {
    const {
      field: { value, onChange },
    } = useController({
      name: 'content_type',
      control: form.control,
    });

    return (
      <FormControl variant={vtrIdChosenForContent ? 'filled' : 'outlined'} fullWidth>
        <InputLabel id="content_type">Content Type</InputLabel>
        <Select
          disabled={vtrIdChosenForContent}
          labelId="content_type"
          id="content_type"
          name="content_type"
          native
          value={value}
          onChange={onChange}
          label="Content Type">
          <option value={Not_Selected_Val}>Select an Option.</option>
          <option value={ContentTypeEnum.MOVIE}>Movie</option>
          <option value={ContentTypeEnum.SERIES}>TV Series</option>
          <option value={ContentTypeEnum.BOOK}>Book</option>
          <option value={ContentTypeEnum.FORMAT}>Format</option>
          <option value={ContentTypeEnum.GAME}>Game</option>
          <option value={ContentTypeEnum.PODCAST}>Podcast</option>
          <option value={ContentTypeEnum.STORY}>Story</option>
          <option value={ContentTypeEnum.IP}>IP</option>
          <option value={ContentTypeEnum.MICROSERIES}>Micro-Series</option>
        </Select>
      </FormControl>
    );
  }, [vtrIdChosenForContent]);

 /*  const AssociatedPerson = useCallback(() => {
    return (
      <ControlledAutoCompleteFieldPersonSearch
        name="cast_and_crew"
        label="Cast and Crew"
        control={form.control}
      />
    );
  }, []); */

  const genres = form.watch('genres_id');

  const { loading: logsLoading, result: genresMaster } = useRequest(() => ContentGenres.get(), []);

  useEffect(() => {
    if (genres && genres.length > 0) {
      const filterGenreCategory = genresMaster?.data.find(genre => genre.id == genres[0].id);
      const categoryValue = filterGenreCategory && filterGenreCategory.genre_catagory_name;
      if (categoryValue) {
        form.setValue('genre_category', categoryValue.toLowerCase());
      }
    } else {
      form.setValue('genre_category', '');
    }
  }, [genres]);

  const nonIDcontentAttrs = useMemo(
    () => [
      createAttr(false, 'title', 'The title of the content.'),
      createAttr(false, 'working_title'),
      createAttr(false, 'content_type', 'Content Type', <ContentType />),
      createAttr(
        false,
        'status',
        'Content Status',
        <ContentStatusField name="status" control={form.control} />
      ),
      createAttr(
        false,
        'genres_id',
        'Genre',
        <GenresField name="genres_id" control={form.control} />
      ),
      createAttr(
        false,
        'origin_country',
        'Country of Origin',
        <OriginCountryField name="origin_country" control={form.control} />
      ),
      createAttr(
        false,
        'genre_category',
        'Genre category',
        <GenresTypeField name="genre_category" control={form.control} />
      ),
      createAttr(
        false,
        'language_id',
        'Language',
        <LanguageField name="language_id" control={form.control} />
      ),
      createAttr(
        false,
        'filming_location',
        'Filming Location',
        <FilmingLocationField name="filming_location" control={form.control} />
      ),
      createAttr(
        false,
        'content_keywords',
        'keywords',
        <ControlledAutoCompleteFieldForContentKeywords
          name="content_keywords"
          control={form.control}
        />
      ),
    //  createAttr(false, 'cast_and_crew', 'Cast and Crew', <AssociatedPerson />),
      // createAttr(false, 'other_internal_fields', 'Internal Info', <AssociatedPerson />),
    ],
    []
  );

/*   useEffect(() => {

    if (isNonIdContent) {
      form.setValue('title', searchText);
    }
  }, [isNonIdContent, searchText]); */

  return (
    <FormProvider {...form}>
      <form onSubmit={createUnIdidContent}>
        <Dialog
          fullWidth
          maxWidth={'md'}
          open={open}
          onClose={onClose}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description">
          <DialogTitle id="alert-dialog-content">Content New Content</DialogTitle>
          <DialogContent dividers>
            <Grid container spacing={2}>
              {nonIDcontentAttrs.map(({ readonly, label, key, component }, idx) => (
                <Grid key={key} item xs={6}>
                  {component ? (
                    component
                  ) : (
                    <ControlledTextField
                      fullWidth
                      variant="outlined"
                      rowsMax={4}
                      name={key}
                      label={label}
                      control={form.control}
                      readonly={readonly}
                      key={idx}
                    />
                  )}
                </Grid>
              ))}
            </Grid>
            <div id="content-form"></div>
          </DialogContent>
          <DialogActions>
            <Button onClick={onCancel} color="primary" disabled={loading}>
              Cancel
            </Button>
            <Button onClick={createUnIdidContent} color="primary" autoFocus disabled={loading}>
              {loading ? (
                <PageLoading style={{ height: 'unset' }} sizeOfCircularProgress={'30px'}>
                  {' '}
                </PageLoading>
              ) : (
                'Ok'
              )}
            </Button>
          </DialogActions>
        </Dialog>
      </form>
    </FormProvider>
  );
}

const createAttr = (
  readonly: boolean = false,
  key: keyof DealEntity,
  label: string = null,
  componentToShow: React.ReactNode = null
) => ({
  readonly: readonly,
  key,
  label: label ? label : startCase(key),
  component: componentToShow,
});
const ControlledTextField = ({ name, control, readonly = false, ...props }) => {
  const {
    field: { value, onChange },
  } = useController({
    name,
    control,
    // , defaultValue: defaultValue
  });
  const callbackOnChange = useCallback(e => onChange(e.target.value), [onChange]);
  return (
    <TextField
      {...props}
      id={`controlled-${name}`}
      name={name}
      value={value}
      InputLabelProps={{ shrink: value ? true : false }}
      inputProps={{ readOnly: readonly }}
      onChange={callbackOnChange}
    />
  );
};

export const ScrollSectionTitle = ({ children, icon, subTitle, id }) => (
  <Grid container id={id} spacing={icon ? 2 : 0} alignItems="center">
    {icon && <Grid item>{icon}</Grid>}
    <Grid item>
      <Typography variant="h5">{children}</Typography>
      {subTitle && (
        <Typography color="textSecondary" variant="body2">
          {subTitle}
        </Typography>
      )}
    </Grid>
  </Grid>
);
